import * as React from 'react';
import { pipe, tuple, Lazy } from 'fp-ts/lib/function';
import { URIS as URISG, Kind } from 'fp-ts/lib/HKT';
import * as A from 'fp-ts/lib/Array';
import * as O from 'fp-ts/lib/Option';
import { Signal, useIORef, IORefHook, useRunOnlyFirst, useSignal } from './hooks';

type RecordArrayed<T> = Record<string, T[]>;

type AnyProps = Record<string, any>;
export type AnyComponent = React.ReactType;

export type AnySlot = {
    type: 'layout' | 'plugin',
    used: boolean,
    props: AnyProps,
    component: AnyComponent,
}




type InjectRecord<F extends URISG, R extends Record<string, any>> = {
    [K in keyof R]: Kind<F, R[K]>
}

type Arrayed<R extends Record<string, any>> = InjectRecord<A.URI, R>;
type IsString<A> = A extends string ? A : never;

export interface URItoKind {}

type URIS = keyof URItoKind;

type Take<K extends URIS, A extends keyof URItoKind[K]> = URItoKind[K][A];

type TakeSlots<K extends URIS> =        Take<K, 'slots'>;
type TakeControllers<K extends URIS> =  Take<K, 'controllers'>;
type TakeEffects<K extends URIS> =      Take<K, 'effects'>;
type TakePlugins<K extends URIS> =      Take<K, 'plugins'>;

type IfRecord<R, A extends string> = R extends { [A: string]: any } ? R[A] : never;

type TakeFromSlot<K extends URIS, T extends keyof TakeSlots<K>, A extends string> = IfRecord<TakeSlots<K>[T], A>;
type TakeFromPlugin<K extends URIS, T extends keyof TakePlugins<K>, A extends string> = IfRecord<TakePlugins<K>[T], A>;

export type TakeInnerProps<K extends URIS, T extends keyof TakeSlots<K>> = TakeFromSlot<K, T, 'innerProps'>
export type TakeOuterProps<K extends URIS, T extends keyof TakeSlots<K>> = TakeFromSlot<K, T, 'outerProps'>
type TakePluginTarget<K extends URIS, T extends keyof TakePlugins<K> = keyof TakePlugins<K>> = TakeFromPlugin<K, T, 'target'>
type TakePluginProps<K extends URIS, T extends keyof TakePlugins<K> = keyof TakePlugins<K>> = TakeFromPlugin<K, T, 'props'>


type SlotComponent<
    K extends URIS,
    T extends keyof TakeSlots<K>
> = React.FunctionComponent<TakeInnerProps<K, T> & TakeOuterProps<K, T>>;

type Slot<T extends Record<string, any>> = {
    type: 'layout' | 'plugin',
    used: boolean,
    props: T,
    component: React.FunctionComponent<T>,
}

type Plugin<T extends Record<string, any>> = {
    type: 'plugin',
    used: boolean,
    props: T,
    component: React.FunctionComponent<T>,
}

type Slots<
    K extends URIS, 
    S extends keyof TakeSlots<K> = keyof TakeSlots<K>,
> = { 
        [T in S]: {
            default: Slot<TakeOuterProps<K, T>>,
            main: Slot<TakeOuterProps<K, T>>[],
        }
    }


type LazySlots<
    K extends URIS,
    S extends TakeSlots<K> = TakeSlots<K>,
> = {
        [T in keyof S]: {
            props: {
                [Z in keyof IfRecord<S[T], 'outerProps'>]: Lazy<IfRecord<S[T], 'outerProps'>[Z]>
            } & {
                [Z in keyof IfRecord<S[T], 'innerProps'>]: Lazy<IfRecord<S[T], 'innerProps'>[Z]>
            },
            component: React.FunctionComponent<IfRecord<S[T], 'outerProps'> & IfRecord<S[T], 'innerProps'>>
        }
    }

type Controllers<
    K extends URIS,
    S extends TakeControllers<K> = TakeControllers<K>,
> = {
        [T in keyof S]: {
            controller: {
                [U in keyof S[T]]: S[T][U]
            }
        }
    }

export type Effects<
    K extends URIS, 
    C extends TakeEffects<K>[number] = TakeEffects<K>[number],
> = {
    [U in C]: Signal
};

type EffectsMap<K extends URIS> = (keyof Effects<K>)[];

type Plugins<
    K extends URIS,
    C extends keyof TakePlugins<K> = keyof TakePlugins<K>,
> = {
    [U in C]: Plugin<TakePluginProps<K, U>>
}
export interface ISlotContext<
    K extends URIS,
    S extends Slots<K> = Slots<K>,
    I extends IORefHook<S> = IORefHook<S>,
    A extends IORefHook<any> = IORefHook<any>,
    N extends IORefHook<Record<keyof S, number>> = IORefHook<Record<keyof S, number>>,
> {
    getSlots: I['read'],
    modifySlots: I['modify'],
    getAccProps: A['read'],
    modifyAccProps: A['modify'],
    getId: N['read'],
    modifyId: N['modify'],
}

export interface IControllerContext<
    K extends URIS,
    S extends TakeControllers<K> = TakeControllers<K>,
    I extends IORefHook<S> = IORefHook<S>,
>{
    getControllers: I['read'],
    modifyControllers: I['modify'],
}

export interface IEffectContext<
    K extends URIS,
    S extends Effects<K> = Effects<K>,
    I extends IORefHook<S> = IORefHook<S>,
>{
    getEffects: I['read'],
}

export interface IPluginContext<
    K extends URIS,
    S extends Arrayed<Plugins<K>> = Arrayed<Plugins<K>>,
    I extends IORefHook<S> = IORefHook<S>,
>{
    getPlugins: I['read'],
    modifyPlugins: I['modify'],
}


export const useSlots = <
    K extends URIS, 
    D extends LazySlots<K> = LazySlots<K>,
    DE extends Slots<K> = Slots<K>,
    DT extends Record<keyof DE, number> = Record<keyof DE, number>,
>(defaultSlots: D): ISlotContext<K, DE> => {

    const {
        read: getSlots,
        modify: modifySlots,
    } = useIORef(Object.entries(defaultSlots).reduce((acc, [key, { props, component }]: any) => {
        return {
            ...acc,
            [key]: {
                default: {
                    component,
                    props: Object.entries(props).reduce((acc, [key, prop]) => {
                        return {
                            ...acc,
                            [key]: (prop as any)(),
                        }
                    }, {}),
                },
                main: [],
            },
        }
    }, {} as DE));

    const {
        read: getId,
        modify: modifyId,
    } = useIORef(Object.keys(defaultSlots).reduce((acc, key) => {
        return {
            ...acc,
            [key]: -1,
        }
    }, {} as DT));

    const {
        read: getAccProps,
        modify: modifyAccProps,
    } = useIORef({  });

    return { 
        getSlots,
        modifySlots,

        getId,
        // @ts-ignore
        modifyId,

        getAccProps,
        modifyAccProps,
    }
}

export const useControllers = <
    K extends URIS, 
    T extends TakeControllers<K> = TakeControllers<K>,
>(defaultControllers: T): IControllerContext<K, T> => {

    const {
        read: getControllers,
        modify: modifyControllers,
    } = useIORef(defaultControllers);

    return {
        getControllers,
        modifyControllers,
    }
}

export const useEffects = <
    K extends URIS, 
    D extends EffectsMap<K> = EffectsMap<K>,
    DE extends Effects<K> = Effects<K>,
>(effectsKeys: D): DE => {

    const effects = effectsKeys.reduce((acc, nameEffect) => {
        return {
            ...acc,
            [nameEffect]: useSignal(),
        }
    }, {} as DE);

    return effects;
}

export const usePlugins = <
    K extends URIS, 
    D extends  Plugins<K> = Plugins<K>,
    DE extends Arrayed<Plugins<K>> = Arrayed<Plugins<K>>,
>(plugins: D): IPluginContext<K, DE> => {

    const {
        read: getPlugins,
        modify: modifyPlugins,
    } = useIORef(Object.entries(plugins).reduce((acc, [key, plugin]) => {
        return {
            ...acc,
            [key]: [plugin],
        }}, {} as DE));

    return {
        getPlugins,
        modifyPlugins,
    }
}


// -------------------------------------------------------------------------------------
// Layout
// -------------------------------------------------------------------------------------

type SlotAny = {
    type: 'layout' | 'plugin',
    used: boolean,
    props: Record<string, any>,
    component: React.ReactType,
}

const push: <T>(index: number, x: T) => (xs: T[]) => T[] = (index, x) => (xs) => {
    const xsW = [...xs];

    if(xsW[index]){
        xsW[index] = x;
    }
    else {
        return xsW.concat(x);
    }

    return xsW;
}

const insertAt = <K extends URIS, X extends Slots<K>>(name: keyof X, index: number, value: { type: 'layout' | 'plugin', used: boolean }) => 
    (x: X) =>
        pipe(
            x,
            x => x[name],
            x => x.main,
            push(index, value),
            (main) => {
                return {
                    ...x,
                    [name]: {
                        ...x[name],
                        main,
                    }
                }
            },
        );

const appendlayoutSlotAt = <K extends URIS, X extends Slots<K>>(name: keyof X, index: number, component: React.FunctionComponent<any>) => 
    (x: X) => 
        pipe(
            x,
            x => x[name],
            x => x.main,
            A.modifyAt(index, (x) => ({ ...x, component })),
            O.fold(
                () => x,
                (main) => {
                    return {
                        ...x,
                        [name]: {
                            ...x[name],
                            main,
                        }
                    }
                }
            )
        );

const appendlayoutPropsAt = <K extends URIS, X extends Slots<K>>(name: keyof X, index: number, props: any) => 
    (x: X) => 
        pipe(
            x,
            x => x[name],
            x => x.main,
            A.modifyAt(index, (x) => {
                return { ...x, props }
            }),
            O.fold(
                () => x,
                (main) => {
                    return {
                        ...x,
                        [name]: {
                            ...x[name],
                            main,
                        }
                    }
                }
            )
        );

const inc = <K extends URIS, X extends keyof Slots<K>, T extends Record<X, number>>(name: X) => 
    (x: T) => ({ ...x, [name]: x[name] + 1 });

export const makeLayoutSlot = 
    <K extends URIS>(SlotContext: React.Context<ISlotContext<K>>) => 
        <S extends keyof Slots<K> = keyof Slots<K>>(name: IsString<S>) => 
            (Component: SlotComponent<K, S>): React.FunctionComponent<TakeOuterProps<K, S>> => {

                return (props) => {
                    
                    const ctx = React.useContext(SlotContext);

                        // @ts-ignore
                    useRunOnlyFirst(ctx.modifyId(inc(name)))();
    
                    const child = {
                        type: 'layout' as const,
                        used: false,  
                    }

                    const id = React.useMemo(() => ctx.getId()[name], []);

                    const defaultAdded = ctx.modifySlots(insertAt(name, id, child));
                    const added = ctx.modifySlots(appendlayoutSlotAt(name, id, Component));
                    const addedProps = ctx.modifySlots(appendlayoutPropsAt(name, id, props));

                    useRunOnlyFirst(defaultAdded)();
                    useRunOnlyFirst(added)();
                    addedProps();

                    return null;
                }
            }
                

const concatPluginProps: (props: AnyProps) => (x: AnyProps) => AnyProps =
    (props) => (x) => ({ ...x, ...props });

type AnyTap = {
    default: AnySlot,
    main: AnySlot[],
}

const takeLastPlugin = <T extends Record<string, AnyTap>>(name: keyof T) => (slots: T) => {
    return pipe(
        slots,
        s => s[name],
        s => s.main,
        A.findLastIndex(x => x.type === 'plugin' && !x.used),
    );
}

const usedAtIndexSlot = <T extends Record<string, AnyTap>>(name: keyof T, index: number) => (slots: T) => {
    return pipe(
        slots,
        s => s[name],
        s => s.main,
        A.modifyAt(index, m => ({ ...m, used: true })),
        O.fold(
            () => slots,
            (main) => {
                return {
                    ...slots,
                    [name]: {
                        ...slots[name],
                        main,
                    },
                }
            },
        )
    );
}

const resetAllSlot = <T extends Record<string, AnyTap>, K extends keyof T>(name: K) => (slots: T) => {
    return pipe(
        slots,
        s => s[name],
        s => s.main,
        s => A.map((s: AnySlot) => ({ ...s, used: false }))(s),
        main => {
            return {
                ...slots,
                [name]: {
                    ...slots[name],
                    main,
                },
            }
        }
    );
}

export const makeLayout = <
    K extends URIS,
    S extends keyof Slots<K> = keyof Slots<K>,
>(SlotContext: React.Context<ISlotContext<K>>): React.FunctionComponent<{ name: S } & TakeInnerProps<K, S>> =>
        ({ name, children, ...rest }) => {


            const ctx = React.useContext(SlotContext);

            const r = pipe(
                ctx.getSlots(),
                (slots) => pipe(
                    slots,
                    takeLastPlugin(name),
                    // смотрим есть ли использованные плагины
                    O.fold(
                        // если нет, то работаем с основной лентой
                        () => pipe(
                            slots,
                            s => s[name as S].main,
                            A.filter(s => s.type === 'layout'),
                            s => s.length === 0 ? O.none : O.some(s),
                            // если в основной ленте ничего нет
                            O.fold(
                                // достаём из дефолтной
                                () => pipe(
                                    slots,
                                    s => s[name as S].default,
                                    s => tuple(s.props, s.component),
                                    ([props, Slot]) => () => {
                                        const fromPlugins = ctx.getAccProps();
                                        return (
                                            <Slot 
                                                {...props} 
                                                {...rest} 
                                                {...fromPlugins}
                                            />
                                        )
                                    },
                                ),
                                // рендерим слоты из основы
                                // сняв метки с плагинов что они использованы
                                (main) => () => {
                                    ctx.modifySlots(resetAllSlot(name))();
                                    return (
                                        <React.Fragment>
                                            {main.map(({ props, component: Slot }) => {
                                                const fromPlugins = ctx.getAccProps();
                                                return (
                                                    <Slot 
                                                        {...props} 
                                                        {...rest}
                                                        {...fromPlugins}
                                                    />
                                                );
                                            })}
                                        </React.Fragment>
                                    );
                                },
                            ),
                        ),
                        // если есть то нужно достать его
                        (index) => pipe(
                            slots,
                            s => s[name as S].main,
                            s => s[index],
                            s => tuple(s.props, s.component),
                            ([props, Plugin]) => () => {
                                
                                ctx.modifySlots(usedAtIndexSlot(name as S, index))();
                                ctx.modifyAccProps(concatPluginProps(rest))();

                                return <Plugin {...props} />
                            },
                        ),
                    ),
                )
            );

            return r();
        };


// -------------------------------------------------------------------------------------
// Controller
// -------------------------------------------------------------------------------------

export const makeController = <K extends URIS>(ControllersContext: React.Context<IControllerContext<K>>) => {

        const useControllerGetter = <C extends keyof TakeControllers<K>>(name: C) => {
            const ctx = React.useContext(ControllersContext);
            const c = ctx.getControllers();

            return c[name];
        }

        const useControllerSetter = <T extends keyof Controllers<K>>(name: T, controller: Partial<TakeControllers<K>[T]>) => {
            const ctx = React.useContext(ControllersContext);

            const run = ctx.modifyControllers(x => {
                return {
                    ...x,
                    [name]: {
                        ...x[name],
                        ...controller,
                    }
                }
            });

            run();
        }

        return tuple(useControllerGetter, useControllerSetter);
    }


// -------------------------------------------------------------------------------------
// Effects
// -------------------------------------------------------------------------------------

export const effectGetter = <K extends URIS>(EffectContext: React.Context<Effects<K>>) => 
    <C extends keyof Effects<K>>(name: C) => {
        const ctx = React.useContext(EffectContext);
        return ctx[name];
    }


// -------------------------------------------------------------------------------------
// Plugins
// -------------------------------------------------------------------------------------

type Ctxes<K extends URIS> = { 
    SlotContext: React.Context<ISlotContext<K>>, 
    PluginContext: React.Context<IPluginContext<K>>,
};


type PluginAny = {
    type: 'plugin',
    props: Record<string, any>,
    component: React.ReactType,
}

type PluginsAny = Record<string, PluginAny[]>;

type TypePlugin = 'slot' | 'plugin';

const getType: (target: string | null) => TypePlugin = 
    (target) => 
        target === null 
            ? 'plugin' 
            : 'slot';

type O = Arrayed<Plugins<'Modal'>> extends RecordArrayed<PluginAny> ? true : false;

const recordInsert: <T, R extends RecordArrayed<T>>(key: keyof R, value: T) => (x: R) => R = 
    (key, value) => (x) => {
        if(!x[key]){
            return {
                ...x,
                [key]: [value],
            }
        }

        return {
            ...x,
            [key]: [...x[key], value],
        }
    }

const appendPlugin = <T extends PluginsAny>(name: keyof T, plugin: PluginAny) =>
    (x: T) => {
        return recordInsert(name, plugin)(x);
    }

const appendlayoutSlot = <K extends URIS, X extends Slots<K>>(name: keyof X, slot: SlotAny) => 
    (x: X) => {
        return {
            ...x,
            [name]: {
                ...x[name],
                main: [...x[name].main, slot]
            },
        }
    }

export const makePluginSlot =
    <K extends URIS>(ctxes: Ctxes<K>) => 
        <
            PluginName extends keyof Plugins<K>,
            Target extends TakePluginTarget<K, PluginName>
        >(name: PluginName, target: Target | null) => (Plugin: React.FunctionComponent<TakePluginProps<K, PluginName>>) =>
            ({ ...props }) => {

                const ctxSlot = React.useContext(ctxes.SlotContext);
                const ctxPlugin = React.useContext(ctxes.PluginContext);

                const child = {
                    type: 'plugin' as const,
                    used: false,
                    props,
                    component: Plugin,   
                }

                const type = getType(target);

                const add = (type: TypePlugin) => {
                    switch(type){
                        case 'plugin': {
                            return ctxPlugin.modifyPlugins(appendPlugin(name, child));
                        }
                        case 'slot': {
                            return ctxSlot.modifySlots(appendlayoutSlot(target as Target, child));
                        }
                    }
                }

                useRunOnlyFirst(add(type))();

                return null;
            }


export const makePlugin = 
    <K extends URIS>(PluginContext: React.Context<IPluginContext<K>>): React.FunctionComponent => 
        () => {

            const ctx = React.useContext(PluginContext);

            const plugins = ctx.getPlugins();

            const pluginsA = React.useMemo(() => Object.values(plugins).map((plugin) => {
                // @ts-ignore
                return plugin.map(({ props, component }: any, indexJ: number) => {
                    return component;
                })
            }), []);

            return (
                <React.Fragment>
                    {Object.values(plugins).map((plugin, indexI) => {
                        // @ts-ignore
                        return plugin.map(({ props }: any, indexJ: number) => {
                            const Plugin = pluginsA[indexI][indexJ];
                            return (
                                <Plugin
                                    key={indexI + indexJ}
                                    {...props}
                                />
                            )
                        })
                    })}
                </React.Fragment>
            );
        }






export const Mock = [];

export const makeEntry = <
    K extends URIS,
    DS extends LazySlots<K> = LazySlots<K>,
    DC extends TakeControllers<K> = TakeControllers<K>,
    DE extends EffectsMap<K> = EffectsMap<K>,
    DP extends Plugins<K> = Plugins<K>,
>(p: {
    SlotContext: React.Context<ISlotContext<K>>, 
    initSlots: DS,
    ControllerContext: React.Context<IControllerContext<K>>,
    initControllers: DC,
    EffectContext: React.Context<Effects<K>>,
    initEffect: DE,
    PluginContext: React.Context<IPluginContext<K>>,
}): React.FunctionComponent<{ connectLayout: React.ReactNode }> => 
    ({ connectLayout, children }) => {

        const slots =        useSlots<K>(p.initSlots);
        const controllers =  useControllers<K>(p.initControllers);
        const effects =      useEffects<K>(p.initEffect);
        const plugins =      usePlugins<K>({} as DP);

        return (
            <p.ControllerContext.Provider value={controllers}>
                <p.SlotContext.Provider value={slots}>
                    <p.PluginContext.Provider value={plugins}>
                        <p.EffectContext.Provider value={effects}>
                            {connectLayout}
                            {children}
                        </p.EffectContext.Provider>
                    </p.PluginContext.Provider>
                </p.SlotContext.Provider>
            </p.ControllerContext.Provider>
        );
    }



export const IdentityComponent: React.FunctionComponent = ({ children }) => {
    return (
        <React.Fragment>{children}</React.Fragment>
    );
}

/**
 * Имеем две ленты слотов
 * 1 - с дефолтным слотом
 * 2 - заполняемая из рендера (основа)
 * 
 * при каждом рендере слота, тот отдаёт пропсы и сам компонент в основу ленты, 
 * помечая, что это лайаут слот
 * 
 * плагин при рендере делает также, только помечает, что это плагин
 * 
 * 
 * основной же компонент лайаут пробегается по основе с конца, берёт на каждой итерации
 * плагиновый слот, рендерит с его внешними пропсами и аккумулируя внутренние
 * и так пока не останется ни одного плагина в ленте
 * простые слоты же рендерит в прямом порядке циклом, прокидывая 
 * в них свои пропсы, их пропсы и накопленные пропсы с плагинов
 * если слотов не оказалось, то берём дефолтный слот
 */